import { GetStaticPaths, GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import CustomPage from '@/components/CustomPage';
import { CustomPage as CustomPageType, CustomRouteWithPage, Namespaces } from '@/types/pages';
import React from 'react';
import { getDraftOrPageContent } from '@/utils/helpers';

export const getStaticPaths: GetStaticPaths = async () => {
  const apiService = new ApiService();
  const postList = await apiService.getCustomPageRoutes(Namespaces.ABOUT);

  return {
    paths: postList.map((route) => ({
      params: { slug: route.slug.current.split('/') },
    })),
    // 'blocking' so an unknown slug gets a real 404 on its first request; with
    // `true` Next served the 200 loading shell until the notFound was cached (FOX2-202).
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params: { slug }, preview = null }) => {
  const apiService = new ApiService();
  const handle = Array.isArray(slug) ? slug.join('/') : [slug].join('/');
  const [data, { companyValues }, menuData, { footer }] = await Promise.all([
    apiService.getCustomPageContent(handle as string, preview, Namespaces.ABOUT),
    apiService.getHomeData(!!preview),
    apiService.getMenuData(),
    apiService.getMiscData(),
  ]);
  const pageData = getDraftOrPageContent(data);
  if (!pageData?.content) {
    return { notFound: true };
  }
  return {
    props: {
      content: pageData.content,
      isDraft: pageData.isDraft ?? false,
      menuData,
      footer,
      slug,
      companyValues,
    },
    revalidate: 3600,
  };
};

interface CMSTestProps {
  content: CustomRouteWithPage;
  menuData: unknown;
  footer: unknown;
  slug: string[];
  isDraft: boolean;
  companyValues: string[];
}

const CMSTest: React.FC<CMSTestProps> = (props) => {
  const { content, menuData, footer, slug, isDraft, companyValues } = props;

  if (companyValues !== undefined && companyValues.length > 0) {
    const page = content.page as CustomPageType;
    if (page && page.content) {
      if (page.content.find((c) => c._key === 'companyValuesItems') === undefined) {
        page.content.push({
          _key: 'companyValuesItems',
          _type: 'companyValuesList',
          items: companyValues as unknown,
        } as (typeof page.content)[0]);
      }
    }
  }

  const ogUrl = `https://foxbox.com/about/${Array.isArray(slug) ? slug.join('/') : slug}`;
  return (
    <CustomPage content={content} menuData={menuData} footer={footer} slug={slug} isDraft={isDraft} ogUrl={ogUrl} />
  );
};

export default CMSTest;
